/** @jsxImportSource @emotion/react */
import { useRouterStore } from 'mobx-state-router';
import React, { useEffect, useState } from 'react';
import useSWR from 'swr';
import useSWRImmutable from 'swr/immutable';

import { Select } from 'src/common/ui/components';
import { ProviderLookupQuery } from 'src/generated/gql/graphql';
import { LOOKUP_PROVIDERS } from 'src/stores/queries/userSearch';

type FilterRoles = 'coordinator' | 'prescriber' | 'peer' | 'caseManager';

type ProviderOption = {
  label?: string;
  value: string;
  role?: FilterRoles;
};

const getProviderOption = (
  provider: ProviderLookupQuery['providerLookup'][number],
  teamRole: FilterRoles,
): ProviderOption => ({
  label: `${provider.firstName} ${provider.lastName}`,
  value: provider.id,
  role: teamRole,
});

export const FilterProvider = ({
  label,
  onChange,
  teamRole,
}: {
  label: string;
  onChange: (option: ProviderOption | null) => void;
  teamRole: FilterRoles;
}) => {
  const routerStore = useRouterStore();

  const [providerSearchTerm, setProviderSearchTerm] = useState<string>('');
  const { data: allProviders } = useSWRImmutable<ProviderLookupQuery>([
    LOOKUP_PROVIDERS,
    { q: '' },
  ]);
  const { data: searchProviders } = useSWR<ProviderLookupQuery>([
    LOOKUP_PROVIDERS,
    { q: providerSearchTerm },
  ]);
  const [selectedProvider, setSelectedProvider] = useState<ProviderOption | null | undefined>(
    undefined,
  );

  useEffect(() => {
    const filters: Record<string, string | undefined> = {};
    filters[teamRole] =
      selectedProvider === null
        ? undefined
        : selectedProvider?.value || routerStore.routerState.queryParams[teamRole];

    routerStore.setRouterState({
      ...routerStore.routerState,
      queryParams: {
        ...routerStore.routerState.queryParams,
        ...filters,
      },
    });
  }, [routerStore, selectedProvider, teamRole]);

  useEffect(() => {
    const currentProvider =
      allProviders?.providerLookup?.find(
        provider => provider.id === routerStore.routerState.queryParams[teamRole],
      ) || null;

    if (!currentProvider) {
      return;
    }

    const currentProviderOption: ProviderOption = getProviderOption(currentProvider, teamRole);
    setSelectedProvider(currentProviderOption);

    // Disable the warning for exhaustive deps so that this effect only runs once
    // after getting the initial data. `allProviders` is fetched as a immutable query
    // and will not change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allProviders]);

  useEffect(() => {
    onChange(selectedProvider || null);
  }, [onChange, selectedProvider]);

  return (
    <Select
      id="provider"
      onInputChange={(event, value) => setProviderSearchTerm(value)}
      label={label}
      options={
        searchProviders?.providerLookup?.map(provider => getProviderOption(provider, teamRole)) ||
        []
      }
      onChange={(event, value) => {
        setSelectedProvider(value || null);
      }}
      value={selectedProvider || null}
    />
  );
};
